import React from 'react';
import { MappedPosition, RawSourceMap, SourceMapConsumer } from 'source-map-js';
import 'whatwg-fetch';

export type SourceMapLocation = {
    location: string;
    line?: number;
    column?: number;
    stackError?: MappedPosition;
};

/**
 * @param namespace used in the request path to get the app.js.map, for example: 'dagligbank-lokalbank-web'. Will turn into a request with the path of '/dagligbank-lokalbank-web/app.js.map'
 */
export default (namespace: string) =>
    async (errorInfo: React.ErrorInfo | undefined): Promise<SourceMapLocation | null> => {
        try {
            const lineAndCol =
                errorInfo?.componentStack
                    ?.match(/[:][0-9]*[:][0-9]*/gm)
                    ?.filter((x) => x.length > 0)
                    .map((x) => {
                        const colAndLine = x.substring(1).split(':');

                        return {
                            line: colAndLine[0],
                            column: colAndLine[1],
                        };
                    }) ?? [];

            const line = lineAndCol?.[0]?.line ? Number.parseInt(lineAndCol[0].line, 10) : undefined;
            const column = lineAndCol?.[0]?.column ? Number.parseInt(lineAndCol[0].column, 10) : undefined;
            const sourceMapLocation: SourceMapLocation = {
                location: process.env.NODE_ENV === 'development' ? '/app.js.map' : `/${namespace}/app.js.map`,
                line,
                column,
                stackError: undefined,
            };

            if (errorInfo && typeof line === 'number' && typeof column === 'number') {
                const sourceMap: RawSourceMap = await fetch(sourceMapLocation.location, {
                    headers: { 'Content-Type': 'application/json' },
                }).then((x) => x.json());

                const scm = new SourceMapConsumer({
                    sourceRoot: sourceMap.sourceRoot ?? '',
                    sourcesContent: sourceMap.sourcesContent ?? [],
                    version: sourceMap.version ?? 3,
                    file: sourceMap.file,
                    sources: sourceMap.sources ?? [],
                    mappings: sourceMap.mappings ?? [],
                    names: sourceMap.names ?? [],
                });
                sourceMapLocation.stackError = scm.originalPositionFor({
                    column,
                    line,
                    bias: 1,
                });
            }
            return sourceMapLocation;
        } catch (_) {
            return null;
        }
    };
